import React from "react";
import { Link } from "react-router-dom";

const OrderConfirm = ({ order , currencySymbol }) => {
  if (!order) return null;

  const invoiceNumber = `INV-${String(order.order_id).padStart(6, "0")}`;

  return (
    <div className="order-success-wrapper">
      <div className="order-success-container">
        {/* Success Icon */}
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <svg
              width="64"
              height="64"
              viewBox="0 0 64 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="32" cy="32" r="32" fill="#DCFCE7" />
              <path
                d="M20 32L28 40L44 24"
                stroke="#15803D"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <h1 className="success-title">Order Placed Successfully! 🎉</h1>
        <p className="success-subtitle">
          Thank you for your purchase. We've sent a confirmation email with your invoice to{" "}
          <strong>{order.email || "your email"}</strong>
        </p>

        {/* Order Details Card */}
        <div className="order-details-card">
          <div className="order-detail-row">
            <span className="detail-label">Order Number</span>
            <span className="detail-value">#{String(order.order_id).padStart(6, "0")}</span>
          </div>
          <div className="order-detail-row">
            <span className="detail-label">Invoice Number</span>
            <span className="detail-value">{invoiceNumber}</span>
          </div>
          <div className="order-detail-row">
            <span className="detail-label">Total Amount</span>
            <span className="detail-value total-amount">{currencySymbol}{Number(order.total).toFixed(2)}</span>
          </div>
          <div className="order-detail-row">\
            <span className="detail-label">Payment Method</span>
            <span className="detail-value">
              {order.payment_method === "cod"
                ? "Cash on Delivery"
                : order.payment_method === "bank_transfer"
                ? "Bank Transfer"
                : order.payment_method === "stripe"
                ? "Credit Card (Stripe)"
                : order.payment_method}
            </span>
          </div>
          <div className="order-detail-row">
            <span className="detail-label">Status</span>
            <span className="detail-value status-badge">{order.status || "Pending"}</span>
          </div>
        </div>

        {/* Payment Instructions */}
        {order.payment_method === "cod" && (
          <div className="payment-instruction cod">
            <div className="instruction-icon">💰</div>
            <div className="instruction-content">
              <h3>Cash on Delivery</h3>
              <p>
                Please keep <strong>{currencySymbol}{Number(order.total).toFixed(2)}</strong> ready in cash when
                your order arrives.
              </p>
            </div>
          </div>
        )}

        {order.payment_method === "bank_transfer" && (
          <div className="payment-instruction bank">
            <div className="instruction-icon">🏦</div>
            <div className="instruction-content">
              <h3>Bank Transfer Instructions</h3>
              <p>
                Please transfer <strong>{currencySymbol}{Number(order.total).toFixed(2)}</strong> and include your
                invoice number <strong>{invoiceNumber}</strong> in the reference.
              </p>
              <p className="instruction-note">
                Your order will be processed once we receive your payment.
              </p>
            </div>
          </div>
        )}

        {/* Email Confirmation Notice */}
        <div className="email-notice">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 4L10 11L17 4M3 4H17V16H3V4Z"
              stroke="#6B7280"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            A detailed invoice has been sent to your email. Please check your inbox and spam folder.
          </span>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <Link to={`/account`} className="btn-primary">
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 8V15H3V8M12 5L9 2L6 5M9 2V12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            View Order Details
          </Link>
          <Link to="/shop" className="btn-secondary">
            Continue Shopping
          </Link>
        </div>
      </div>

      <style jsx>{`
        .order-success-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #f3faf2 0%, #ffffff 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
        }

        .order-success-container {
          max-width: 600px;
          width: 100%;
          background: #ffffff;
          border-radius: 16px;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
          padding: 48px 40px;
          text-align: center;
        }

        .success-icon-wrapper {
          margin-bottom: 24px;
        }

        .success-icon {
          display: inline-block;
          animation: successPop 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        @keyframes successPop {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .success-title {
          font-size: 28px;
          font-weight: 800;
          color: #111827;
          margin-bottom: 12px;
          letter-spacing: -0.5px;
        }

        .success-subtitle {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 32px;
        }

        .success-subtitle strong {
          color: #16a34a;
          font-weight: 600;
        }

        .order-details-card {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 24px;
          text-align: left;
        }

        .order-detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #e5e7eb;
        }

        .order-detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-size: 13px;
          color: #6b7280;
          font-weight: 500;
        }

        .detail-value {
          font-size: 14px;
          color: #111827;
          font-weight: 600;
          font-family: "Courier New", monospace;
        }

        .total-amount {
          color: #16a34a;
          font-size: 18px;
        }

        .status-badge {
          background: #dcfce7;
          color: #15803d;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: "Arial", sans-serif;
        }

        .payment-instruction {
          background: #fffbeb;
          border: 2px solid #fbbf24;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 24px;
          display: flex;
          gap: 16px;
          text-align: left;
        }

        .payment-instruction.bank {
          background: #eff6ff;
          border-color: #3b82f6;
        }

        .instruction-icon {
          font-size: 32px;
          flex-shrink: 0;
        }

        .instruction-content h3 {
          font-size: 15px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 8px;
        }

        .instruction-content p {
          font-size: 13px;
          color: #374151;
          line-height: 1.6;
          margin-bottom: 8px;
        }

        .instruction-content p:last-child {
          margin-bottom: 0;
        }

        .instruction-note {
          font-size: 12px;
          color: #6b7280;
          font-style: italic;
        }

        .email-notice {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 14px 18px;
          margin-bottom: 28px;
          font-size: 13px;
          color: #6b7280;
          text-align: left;
        }

        .email-notice svg {
          flex-shrink: 0;
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .btn-primary,
        .btn-secondary {
          flex: 1;
          min-width: 200px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 24px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 700;
          text-decoration: none;
          transition: all 0.2s;
          font-family: "Arial", sans-serif;
        }

        .btn-primary {
          background: #16a34a;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3);
        }

        .btn-primary:hover {
          background: #15803d;
          color: #000000;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.4);
        }

        .btn-secondary {
          background: #ffffff;
          color: #374151;
          border: 2px solid #e5e7eb;
        }

        .btn-secondary:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }

        @media (max-width: 640px) {
          .order-success-container {
            padding: 32px 24px;
          }

          .success-title {
            font-size: 24px;
          }

          .action-buttons {
            flex-direction: column;
          }

          .btn-primary,
          .btn-secondary {
            width: 100%;
            min-width: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default OrderConfirm;